'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerPortal,
	DrawerTitle
} from '@pickleballinc/ui/components/ui/drawer';
import { Icon } from '@pickleballinc/ui/components/ui/icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { useSeasonFromSearchParams } from '@/hooks/filters/useSeasonFromSearchParams';
import { Options } from '@/utils/types';

export interface ISchedulePageFilters {
	season: string;
	division: string;
	district: string;
	mobile_filters: string;
}

interface FiltersForSchedulePageProps {
	seasons: Options[];
	divisions: Options[];
	districts: Options[];
	activeSeason?: string;
}

const getNewSearchParams = (filters: ISchedulePageFilters) => {
	const newSearchParams = new URLSearchParams(window.location.search);

	newSearchParams.delete('current_page');

	if (filters.season) {
		newSearchParams.delete('startDate');
		newSearchParams.delete('endDate');
		newSearchParams.delete('month');
		newSearchParams.set('season', filters.season);
	} else {
		newSearchParams.delete('season');
	}

	if (filters.district && filters.district !== 'all') {
		newSearchParams.set('district', filters.district);
	} else {
		newSearchParams.delete('district');
	}

	if (filters.division && filters.division !== 'all') {
		newSearchParams.set('division', filters.division);
	} else {
		newSearchParams.delete('division');
	}

	return newSearchParams || '';
};

export const FiltersForSchedulePage = ({ seasons, districts, activeSeason, divisions }: FiltersForSchedulePageProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();

	const [filters, setFilters] = useState<ISchedulePageFilters>({
		season: searchParams?.get('season') ?? activeSeason ?? seasons[0]?.value ?? '',
		division: searchParams?.get('division') ?? '',
		district: searchParams?.get('district') ?? '',
		mobile_filters: ''
	});

	// Filtering by division and district is not enabled on the backend. When the backend enables filtering, all that's needed is to uncomment the code

	const divisionsWithAllOption: Options[] = divisions.length >= 1 ? [{ value: 'all', label: 'All divisions' }, ...divisions] : [];
	const districtsWithAllOption: Options[] = districts.length >= 1 ? [{ value: 'all', label: 'All districts' }, ...districts] : [];

	const emptyFilters: ISchedulePageFilters = {
		season: activeSeason ?? seasons[0]?.value ?? '',
		division: '',
		district: '',
		mobile_filters: ''
	};

	const handleReset = () => {
		setFilters(emptyFilters);
		router.replace('?');
	};

	useSeasonFromSearchParams(searchParams, setFilters, activeSeason ?? seasons[0]?.value);

	return (
		<>
			<div
				className={`${districts.length !== 1 ? 'hidden' : 'flex flex-col'} md:grid md:grid-cols-2 gap-3 md:gap-x-6 md:gap-y-4 mt-4 lg:mt-6 lg:mb-2`}
			>
				<div>
					<Select onValueChange={(value) => setFilters({ ...filters, season: value })} value={filters.season || seasons[0]?.value}>
						<SelectTrigger
							className="bg-white focus:ring-0 h-10"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Select season" />
						</SelectTrigger>
						<SelectContent>
							{seasons.map((season) => (
								<SelectItem key={season.value} value={season.value}>
									{season.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="hidden md:block" />
				{districts.length !== 1 && (
					<div className="flex items-start xl:items-center">
						<Select
							onValueChange={(value) => setFilters({ ...filters, district: value })}
							value={filters.district || districtsWithAllOption[0]?.value}
						>
							<SelectTrigger
								id="district-select"
								className={clsx('bg-white focus:ring-0 h-10', {
									hidden: districts.length === 1
								})}
								icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
							>
								<SelectValue placeholder="Select district" />
							</SelectTrigger>
							<SelectContent>
								{districtsWithAllOption.map((district) => (
									<SelectItem key={district.value} value={district.value}>
										{district.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}

				<div
					className={`flex flex-col xl:flex-row gap-3 items-start md:items-end xl:items-end w-full justify-between ${districts.length === 1 && 'col-span-2'}`}
				>
					<div className={`self-start ${districts.length === 1 ? 'w-full md:w-[49%]' : 'w-full'}`}>
						<Select
							onValueChange={(value) => setFilters({ ...filters, division: value })}
							value={filters.division || divisionsWithAllOption[0]?.value}
						>
							<SelectTrigger
								id="division-select"
								className={clsx('bg-white focus:ring-0 h-10', {
									hidden: divisions.length === 1
								})}
								icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
							>
								<SelectValue placeholder="Select division" />
							</SelectTrigger>
							<SelectContent>
								{divisionsWithAllOption.map((division) => (
									<SelectItem key={division.value} value={division.value}>
										{division.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="flex flex-row gap-4">
						<Button onClick={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)} className="font-semibold" size="lg">
							Apply
						</Button>

						<Button onClick={handleReset} variant="outline" size="lg">
							Reset
						</Button>
					</div>
				</div>
			</div>
			<Button
				variant="outline"
				className={`${districts.length !== 1 ? 'flex' : 'hidden'} animate-fade-in-down w-full items-center justify-center mt-5 md:hidden`}
				size="lg"
				onClick={() => setFilters({ ...filters, mobile_filters: 'true' })}
			>
				<FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
				Filters
			</Button>
			<MobileFiltersForSchedulePage
				filters={filters}
				setFilters={setFilters}
				handleReset={handleReset}
				seasons={seasons}
				divisions={divisions}
				districts={districts}
				divisionsWithAllOption={divisionsWithAllOption}
				districtsWithAllOption={districtsWithAllOption}
			/>
		</>
	);
};
interface MobileFiltersForSchedulePageProps {
	filters: ISchedulePageFilters;
	handleReset: () => void;
	setFilters: (filters: ISchedulePageFilters) => void;
	seasons: Options[];
	divisions: Options[];
	districts: Options[];
	divisionsWithAllOption: Options[];
	districtsWithAllOption: Options[];
}

export const MobileFiltersForSchedulePage = ({
	filters,
	handleReset,
	setFilters,
	seasons,
	divisions,
	districts,
	divisionsWithAllOption,
	districtsWithAllOption
}: MobileFiltersForSchedulePageProps) => {
	const router = useRouter();

	const isSliderOpen = filters.mobile_filters === 'true';

	return (
		<Drawer open={isSliderOpen} onClose={() => setFilters({ ...filters, mobile_filters: '' })}>
			<DrawerPortal>
				<DrawerContent className="flex w-full flex-col bg-white px-4 pb-4 pt-1 md:hidden h-[90%]">
					<DrawerDescription className="sr-only">Filters for schedule page</DrawerDescription>
					<div className="overflow-y-auto">
						<DrawerHeader className="flex justify-between">
							<DrawerTitle>Filters</DrawerTitle>
							<DrawerClose>
								<Icon glyph="close" />
							</DrawerClose>
						</DrawerHeader>
						<div className="flex flex-col gap-5">
							<div>
								<Select
									onValueChange={(value) => setFilters({ ...filters, season: value })}
									value={filters.season || seasons[0]?.value}
								>
									<SelectTrigger
										className="bg-white focus:ring-0 h-10"
										icon={({ className }: { className: string }) => (
											<FontAwesomeIcon icon={faChevronDown} className={className} />
										)}
									>
										<SelectValue placeholder="Select season" />
									</SelectTrigger>
									<SelectContent>
										{seasons.map((season) => (
											<SelectItem key={season.value} value={season.value}>
												{season.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="hidden md:block" />

							{districts.length !== 1 && (
								<div className="flex items-start xl:items-center">
									<Select
										onValueChange={(value) => setFilters({ ...filters, district: value })}
										value={filters.district || districtsWithAllOption[0]?.value}
									>
										<SelectTrigger
											id="district-select"
											className="bg-white focus:ring-0 h-10"
											icon={({ className }: { className: string }) => (
												<FontAwesomeIcon icon={faChevronDown} className={className} />
											)}
										>
											<SelectValue placeholder="Select district" />
										</SelectTrigger>
										<SelectContent>
											{districtsWithAllOption.map((district) => (
												<SelectItem key={district.value} value={district.value}>
													{district.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							)}

							<div
								className={`flex flex-col xl:flex-row gap-3 items-start md:items-end xl:items-center w-full justify-between ${districts.length === 1 && 'col-span-2'}`}
							>
								<div className={`self-start ${districts.length === 1 ? 'w-full md:w-[49%]' : 'w-full'}`}>
									<Select
										onValueChange={(value) => setFilters({ ...filters, division: value })}
										value={filters.division || divisionsWithAllOption[0]?.value}
									>
										<SelectTrigger
											id="division-select"
											className={clsx('bg-white focus:ring-0 h-10', {
												hidden: divisions.length === 1
											})}
											icon={({ className }: { className: string }) => (
												<FontAwesomeIcon icon={faChevronDown} className={className} />
											)}
										>
											<SelectValue placeholder="Select division" />
										</SelectTrigger>
										<SelectContent>
											{divisionsWithAllOption.map((division) => (
												<SelectItem key={division.value} value={division.value}>
													{division.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="flex flex-row gap-4 place-self-end">
									<Button
										onClick={() => {
											setFilters({ ...filters, mobile_filters: '' });
											router.replace(`?${getNewSearchParams(filters)?.toString()}`);
										}}
										size="lg"
									>
										Apply
									</Button>

									<Button onClick={handleReset} size="lg" variant="outline">
										Reset
									</Button>
								</div>
							</div>
						</div>
					</div>
				</DrawerContent>
			</DrawerPortal>
		</Drawer>
	);
};
